import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { meetingsAPI } from '../../lib/api';
import { fmtDateOnly, apiErrorText } from './meetingsUtils';

const EMPTY = { text: '', promised_by: 'VSTL', due_date: '', owner_id: '' };

export default function MeetingActionItemsSection({ meetingId, organiserId, organiserName, canAdd, actionItemError, refreshSignal, onActionItemsChanged }) {
  const [actions, setActions] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ ...EMPTY });

  const load = () => meetingsAPI.listActions({ meeting_id: meetingId }).then((r) =>
    setActions(r.data.actions || [])
  ).catch(() => {});

  useEffect(() => { load(); meetingsAPI.searchColleagues('').then((r) => setColleagues(r.data.colleagues || [])).catch(() => {}); }, [meetingId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (refreshSignal) load(); }, [refreshSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form.text.trim() || !form.due_date) { toast.error('Please fill in the action and due date'); return; }
    try {
      await meetingsAPI.addAction(meetingId, { ...form, owner_id: form.owner_id || organiserId });
      toast.success('Action item added');
      setForm({ ...EMPTY }); setAdding(false); load();
      onActionItemsChanged?.();
    } catch (e) { toast.error(apiErrorText(e, 'Failed to add action item')); }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditForm({ text: a.text, promised_by: a.promised_by, due_date: a.due_date, owner_id: a.owner_id });
  };

  const saveEdit = async () => {
    if (!editForm.text.trim() || !editForm.due_date) { toast.error('Please fill in the action and due date'); return; }
    try {
      await meetingsAPI.editDraftAction(meetingId, editingId, editForm);
      toast.success('Draft action item updated');
      setEditingId(null); load();
    } catch (e) { toast.error(apiErrorText(e, 'Failed to update draft')); }
  };

  const removeDraft = async (actionId) => {
    try {
      await meetingsAPI.deleteDraftAction(meetingId, actionId);
      toast.success('Draft action item removed');
      load();
    } catch (e) { toast.error(apiErrorText(e, 'Failed to remove draft')); }
  };

  return (
    <div className="space-y-2" data-testid="meeting-action-items-section">
      <p className="text-sm font-semibold">Action Items</p>
      {actions.length === 0 && <p className="text-xs text-muted-foreground" data-testid="no-meeting-actions-msg">No action items yet.</p>}
      {actions.map((a) => (
        <div key={a.id} className="border rounded-sm px-2 py-1.5" data-testid={`meeting-action-row-${a.id}`}>
          {editingId === a.id ? (
            <div className="grid sm:grid-cols-2 gap-2 p-1" data-testid={`meeting-action-edit-form-${a.id}`}>
              <Input placeholder="What was promised?" value={editForm.text} onChange={(e) => setEditForm({ ...editForm, text: e.target.value })} className="sm:col-span-2" data-testid="meeting-action-edit-text" />
              <Select value={editForm.promised_by} onValueChange={(v) => setEditForm({ ...editForm, promised_by: v })}>
                <SelectTrigger data-testid="meeting-action-edit-promised-by"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="VSTL">We promised</SelectItem><SelectItem value="COUNTERPARTY">They promised</SelectItem></SelectContent>
              </Select>
              <Input type="date" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} data-testid="meeting-action-edit-due-date" />
              <Select value={editForm.owner_id || organiserId} onValueChange={(v) => setEditForm({ ...editForm, owner_id: v })}>
                <SelectTrigger data-testid="meeting-action-edit-owner"><SelectValue placeholder="Owner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={organiserId}>{organiserName} (organiser)</SelectItem>
                  {colleagues.filter((c) => c.id !== organiserId).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex gap-2 sm:col-span-2">
                <Button size="sm" onClick={saveEdit} data-testid="meeting-action-edit-save-btn">Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} data-testid="meeting-action-edit-cancel-btn">Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm">
              <span className="flex-1">{a.text} — {a.owner_name} by {fmtDateOnly(a.due_date)}</span>
              {a.status === 'DRAFT' && a.auto_generated && <Badge variant="secondary" data-testid={`meeting-action-draft-badge-${a.id}`}>Auto-draft</Badge>}
              <Badge variant="outline">{a.status}</Badge>
              {a.status === 'DRAFT' && canAdd && (
                <>
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => startEdit(a)} data-testid={`meeting-action-edit-btn-${a.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeDraft(a.id)} data-testid={`meeting-action-delete-btn-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </>
              )}
            </div>
          )}
        </div>
      ))}
      {actionItemError && (
        <p className="text-xs text-red-700 flex items-center gap-1.5" data-testid="meeting-action-item-error">
          <AlertTriangle className="h-3.5 w-3.5" /> {actionItemError}
        </p>
      )}
      {canAdd && (adding ? (
        <div className="grid sm:grid-cols-2 gap-2 border rounded-sm p-2" data-testid="meeting-action-add-form">
          <Input placeholder="What was promised?" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="sm:col-span-2" data-testid="meeting-action-text" />
          <Select value={form.promised_by} onValueChange={(v) => setForm({ ...form, promised_by: v })}>
            <SelectTrigger data-testid="meeting-action-promised-by"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="VSTL">We promised</SelectItem><SelectItem value="COUNTERPARTY">They promised</SelectItem></SelectContent>
          </Select>
          <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="meeting-action-due-date" />
          <Select value={form.owner_id || organiserId} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
            <SelectTrigger data-testid="meeting-action-owner"><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={organiserId}>{organiserName} (organiser)</SelectItem>
              {colleagues.filter((c) => c.id !== organiserId).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-2 sm:col-span-2">
            <Button size="sm" onClick={save} data-testid="meeting-action-save-btn">Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)} data-testid="meeting-action-cancel-btn">Cancel</Button>
          </div>
        </div>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="meeting-action-add-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add action item
        </Button>
      ))}
    </div>
  );
}
